import axios from 'axios';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { sendRef } from 'src/api/ref';
import { BASE_LANGUAGE, BASE_REGION, FALLBACK_LANGUAGE } from 'src/config/i18next';
import { useAccount } from 'src/core/hooks/account';
import { REGEX_NUMERIC } from 'src/schemas/validations';
import { updateShoppingCart } from 'src/store/cart/actions';
import { enqueueSnackbarAction } from 'src/store/notifications/actions';

export const LANGUAGE_KEYS = [
    'de',
    'en',
    'hr',
    'hu',
    'lv',
    'nl',
    'ru',
    'sl',
    'it',
    'et',
    'lt',
    'se',
    'pl',
    'es',
    'fr',
    'ro',
] as const;
export type LanguageKeys = typeof LANGUAGE_KEYS[number];

export const REGION_KEYS = [
    'at',
    'de',
    'hr',
    'si',
    'it',
    'hu',
    'se',
    'ee',
    'lv',
    'lt',
    'nl',
    'no',
    'pl',
    'ch',
    'es',
    'bg',
    'fr',
    'be',
] as const;
export type RegionKeys = typeof REGION_KEYS[number];

export const TEMPLATE_TYPES = ['PLAIN_TEXT', 'MARKDOWN', 'HTML', 'TEMPLATE_LIST', 'MUSTACHE', 'NAMESPACE'] as const;
export type TemplateTypes = typeof TEMPLATE_TYPES[number];

export const REGION_TEMPLATE_TYPES = [
    'Dokument',
    'E-Mail',
    'Webseite',
    'Video',
    'Produktverpackung',
    'Sonstiges',
] as const;
export type RegionTemplateTypes = typeof REGION_TEMPLATE_TYPES[number];

const [fallback_language, fallback_region] = FALLBACK_LANGUAGE.split('-');

interface TranslationData {
    [translationKey: string]:
        | {
              [locale: string]: string;
          }
        | undefined;
}

export interface IAppMetaContext {
    languageKeys: readonly LanguageKeys[];
    templateTypes: readonly TemplateTypes[];
    regionKeys: readonly RegionKeys[];
    selectedLanguageKey: LanguageKeys;
    setLanguage: (languageKey: LanguageKeys, reinitialize?: boolean) => void;
    selectedRegionKey: RegionKeys;
    setRegion: (regionKey: RegionKeys, reinitialize?: boolean) => void;
    selectedLocale: string;
    setLocale: (languageKey: LanguageKeys, regionKey: RegionKeys, reinitialize?: boolean) => void;
    customerRef: string;
    firstName: string;
    lastName: string;
    email: string;
    landingPageId: string;
    translationMode: boolean;
    toggleTranslationMode: (tm?: boolean) => void;
    translationFocusKey?: string;
    setTranslationFocus: (key?: string) => void;
    translationData: TranslationData;
    getTranslationDataByKey: (key?: string) => Record<string, string> | undefined;
    hasTranslationData: (key: string) => boolean;
    setTranslationData: (translationKey: string, locale: string, value: string) => void;
    removeTranslationData: (translationKey: string) => void;
    initialized: boolean;
    isExternallyAuthenticated: boolean;
}

const AppMetaContext = createContext<IAppMetaContext>({
    languageKeys: LANGUAGE_KEYS,
    templateTypes: TEMPLATE_TYPES,
    regionKeys: REGION_KEYS,
    selectedLanguageKey: fallback_language as LanguageKeys,
    setLanguage: () => {},
    selectedRegionKey: fallback_region as RegionKeys,
    setRegion: () => {},
    selectedLocale: FALLBACK_LANGUAGE,
    setLocale: () => {},
    customerRef: '',
    firstName: '',
    lastName: '',
    email: '',
    landingPageId: '',
    translationMode: false,
    toggleTranslationMode: () => {},
    setTranslationFocus: () => {},
    translationData: {},
    getTranslationDataByKey: () => undefined,
    hasTranslationData: () => false,
    setTranslationData: () => {},
    removeTranslationData: () => {},
    initialized: false,
    isExternallyAuthenticated: false,
});

const AppMetaProvider: React.FC = ({ children }) => {
    const { search } = useLocation();
    const { t, i18n } = useTranslation();
    const { auth } = useAccount();
    const dispatch = useDispatch();

    const [i18nLanguage, i18nRegion] = (i18n.language ?? FALLBACK_LANGUAGE).split('-');
    const language = resolveLanguage(i18nLanguage);
    const region = resolveRegion(i18nRegion, language as LanguageKeys);

    const [selectedLanguageKey, setSelectedLanguageKey] = useState(language as LanguageKeys);
    const [selectedRegionKey, setSelectedRegionKey] = useState(region as RegionKeys);
    const [translationMode, setTranslationMode] = useState(false);
    const [translationFocusKey, setTranslationFocusKey] = useState<string | undefined>();
    const [translationData, setTranslationData] = useState<TranslationData>({});
    const [initializedFromUserProfile, setInitializedFromUserProfile] = useState(false);
    const [initialized, setInitialized] = useState(false);

    // Set Axios Headers
    axios.defaults.headers.common.language = selectedLanguageKey;
    axios.defaults.headers.common.region = selectedRegionKey;

    // Handles initialization
    useEffect(() => {
        i18n.changeLanguage(`${selectedLanguageKey}-${selectedRegionKey}`);
        axios.defaults.headers.common.language = selectedLanguageKey;
        axios.defaults.headers.common.region = selectedRegionKey;
        setInitialized(true);
    }, [i18n, selectedLanguageKey, selectedRegionKey]);

    // Update cart on region change
    useEffect(() => {
        dispatch(updateShoppingCart(undefined, false, undefined));
    }, [selectedRegionKey, dispatch]);

    // Set language and region based on user profile
    useEffect(() => {
        const profile = auth?.userData?.profile;

        if (initializedFromUserProfile || profile == null) return;

        const language = profile.language as LanguageKeys | undefined;

        if (language && language !== selectedLanguageKey) {
            setSelectedLanguageKey(language);
            dispatch(
                enqueueSnackbarAction({
                    message: t('translations:force_changed_language'),
                    options: {
                        key: 'change_language',
                        variant: 'success',
                    },
                })
            );
        }

        const region = profile.region as RegionKeys | undefined;

        if (region && region !== selectedRegionKey) {
            setSelectedRegionKey(region);
            dispatch(
                enqueueSnackbarAction({
                    message: t('translations:force_changed_region'),
                    options: {
                        key: 'change_region',
                        variant: 'success',
                    },
                })
            );
        }

        setInitializedFromUserProfile(true);
    }, [auth?.userData?.profile, dispatch, t, initializedFromUserProfile, selectedLanguageKey, selectedRegionKey]);

    const value = useMemo(
        () => ({
            languageKeys: LANGUAGE_KEYS,
            templateTypes: TEMPLATE_TYPES,
            regionKeys: REGION_KEYS,
            selectedLanguageKey,
            setLanguage: (languageKey: LanguageKeys, reinitialize: boolean = true) => {
                if (languageKey === selectedLanguageKey) return;
                if (reinitialize) setInitialized(false);
                setSelectedLanguageKey(languageKey);
            },
            selectedRegionKey,
            setRegion: (regionKey: RegionKeys, reinitialize: boolean = true) => {
                if (regionKey === selectedRegionKey) return;
                if (reinitialize) setInitialized(false);
                setSelectedRegionKey(regionKey);
            },
            selectedLocale: `${selectedLanguageKey}-${selectedRegionKey}`,
            setLocale: (languageKey: LanguageKeys, regionKey: RegionKeys, reinitialize: boolean = true) => {
                if (languageKey === selectedLanguageKey && regionKey === selectedRegionKey) return;
                if (reinitialize) setInitialized(false);
                setSelectedLanguageKey(languageKey);
                setSelectedRegionKey(regionKey);
            },
            customerRef: getCustomerRef(search),
            firstName: getCustomerFirstName(search),
            lastName: getCustomerLastName(search),
            email: getCustomerEmail(search),
            isExternallyAuthenticated: getAuthenticationStatus(search),
            landingPageId: getLandingPageId(search),
            translationMode,
            toggleTranslationMode: (tm?: boolean) => {
                if (tm === undefined) setTranslationMode(!translationMode);
                else setTranslationMode(tm);
            },
            translationFocusKey,
            setTranslationFocus: (key?: string) => setTranslationFocusKey(key),
            translationData,
            getTranslationDataByKey: (key?: string): Record<string, string> | undefined => {
                return key === undefined ? undefined : translationData[key];
            },
            hasTranslationData: (translationKey: string): boolean => {
                return translationData[translationKey] !== undefined;
            },
            setTranslationData: (translationKey: string, locale: string, value: string) => {
                setTranslationData({
                    ...translationData,
                    [translationKey]: {
                        ...(translationData[translationKey] ?? {}),
                        [locale]: value,
                    },
                });
            },
            removeTranslationData: (translationKey: string) => {
                setTranslationData({
                    ...translationData,
                    [translationKey]: undefined,
                });
            },
            initialized,
        }),
        [
            selectedLanguageKey,
            selectedRegionKey,
            search,
            translationMode,
            translationFocusKey,
            translationData,
            initialized,
        ]
    );

    return <AppMetaContext.Provider value={value}>{children}</AppMetaContext.Provider>;
};

export default AppMetaProvider;

function getCustomerRef(search: string): string {
    const params = new URLSearchParams(search);
    const ref = params.get('ref');

    if (ref && REGEX_NUMERIC.test(ref)) {
        sendRef(ref, window.location.href);
        localStorage.setItem('customerRef', ref);
        return ref;
    } else {
        return localStorage.getItem('customerRef') ?? '';
    }
}

function getCustomerFirstName(search: string): string {
    const params = new URLSearchParams(search);
    const fn = params.get('fn');
    return fn ?? '';
}

function getCustomerLastName(search: string): string {
    const params = new URLSearchParams(search);
    const ln = params.get('ln');
    return ln ?? '';
}

function getCustomerEmail(search: string): string {
    const params = new URLSearchParams(search);
    const email = params.get('email');
    return email ?? '';
}

function getAuthenticationStatus(search: string): boolean {
    const params = new URLSearchParams(search);
    const authStatus = params.get('eauth');

    if (authStatus) {
        const isAuth = authStatus.toLowerCase() === 'true';
        return isAuth;
    }
    return false;
}

function getLandingPageId(search: string): string {
    const params = new URLSearchParams(search);
    const lp = params.get('lp');

    if (lp) {
        localStorage.setItem('landingPageId', lp);
        return lp;
    } else {
        return localStorage.getItem('landingPageId') ?? '';
    }
}

export function useAppMeta() {
    return useContext(AppMetaContext);
}

function resolveLanguage(language): LanguageKeys {
    if (!LANGUAGE_KEYS.includes(language)) return BASE_LANGUAGE;
    return language;
}

function resolveRegion(region, language: LanguageKeys): RegionKeys {
    if (REGION_KEYS.includes(region)) return region;

    switch (language) {
        case 'hr':
            return 'hr';
        case 'hu':
            return 'hu';
        case 'lv':
            return 'lv';
        case 'nl':
            return 'nl';
        case 'sl':
            return 'si';
        case 'it':
            return 'it';
        case 'et':
            return 'ee';
        case 'lt':
            return 'lt';
        case 'se':
            return 'se';
        default:
            return BASE_REGION;
    }
}
